/**
 * Builds a modern Shopify CDN transformation URL while preserving version and
 * other existing query parameters. Keeping this as a leaf module lets the
 * inlined collection loader preload exactly the URLs emitted by Picture.
 */
export const DEFAULT_IMAGE_CROP = 'center';

const TRANSFORM_PARAMETERS = new Set(['width', 'height', 'crop']);

const parseSize = (size, retina) => {
  const match = /^(\d*)x(\d*)(?:_crop_([^?&#]+))?$/.exec(String(size || '').trim());

  if (!match || (!match[1] && !match[2])) {
    return null;
  }

  const multiplier = retina ? 2 : 1;

  return {
    crop: match[3] || null,
    height: match[2] ? Number(match[2]) * multiplier : null,
    width: match[1] ? Number(match[1]) * multiplier : null,
  };
};

const normalizeCrop = (cropType) => {
  if (!cropType) {
    return null;
  }

  return String(cropType).replace(/^crop_/, '');
};

export const buildSizedImageUrl = (
  url,
  size,
  { cropType = DEFAULT_IMAGE_CROP, retina = false } = {}
) => {
  const source = String(url || '').trim();
  const dimensions = parseSize(size, retina);

  if (!source || !dimensions) {
    return source;
  }

  const hashIndex = source.indexOf('#');
  const hash = hashIndex === -1 ? '' : source.substring(hashIndex);
  const sourceWithoutHash = hashIndex === -1 ? source : source.substring(0, hashIndex);
  const queryIndex = sourceWithoutHash.indexOf('?');
  const path = queryIndex === -1
    ? sourceWithoutHash
    : sourceWithoutHash.substring(0, queryIndex);
  const existingQuery = queryIndex === -1
    ? ''
    : sourceWithoutHash.substring(queryIndex + 1);
  const query = existingQuery
    .split('&')
    .filter(Boolean)
    .filter((parameter) => {
      const name = parameter.split('=', 1)[0].toLowerCase();
      return !TRANSFORM_PARAMETERS.has(name);
    });

  if (dimensions.width) {
    query.push(`width=${dimensions.width}`);
  }
  if (dimensions.height) {
    query.push(`height=${dimensions.height}`);
  }

  const crop = normalizeCrop(dimensions.crop || cropType);
  if (crop && dimensions.width && dimensions.height) {
    query.push(`crop=${encodeURIComponent(crop)}`);
  }

  return `${path}?${query.join('&')}${hash}`;
};

export default buildSizedImageUrl;
